<template>
  <div>
    <div v-show="visible">
      <div
        class="relative z-40 lg:hidden"
        role="dialog"
        aria-modal="true"
      >
        <div class="fixed inset-0 bg-gray-600 bg-opacity-90" />
        <div class="fixed inset-0 flex z-40">
          <div class="relative flex flex-col w-64 pt-5 pb-4 bg-white dark:bg-gray-700">
            <div
              class="absolute top-0 right-0 -mr-12 pt-3"
              @click="$emit('hide')"
            >
              <button
                type="button"
                class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <span class="sr-only">Fechar menu</span>
                <span class="text-white">
                  <x-mark-icon class="h-6 w-6" />
                </span>
              </button>
            </div>
            <div class="flex-shrink-0 flex items-center px-4">
              <blitz-logo class="h-8 mr-2 inline" />
              <span class="w-auto text-lg font-semibold dark:text-white">Blitz</span>
            </div>
            <div class="mt-5 flex-1 h-0 overflow-y-auto">
              <nav class="px-2 space-y-1">
                <router-link
                  v-for="link in links"
                  :key="link.route"
                  :to="{ name: link.route }"
                  class="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  :class="{ 'bg-gray-200 dark:bg-gray-600': $route.name === link.route }"
                  @click="$emit('hide')"
                >
                  <span class="mr-2 flex-shrink-0 h-6 w-6 text-gray-600 dark:text-gray-200">
                    <component :is="link.icon || span" />
                  </span>
                  <span>{{ link.title }}</span>
                </router-link>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
      <div
        class="flex flex-col flex-grow border-r border-gray-300 dark:border-gray-600 pt-5 bg-white dark:bg-gray-700 overflow-y-auto"
      >
        <div class="flex items-center flex-shrink-0 px-4">
          <blitz-logo class="h-8 mr-2 inline" />
          <span class="w-auto text-lg font-semibold dark:text-white">Blitz</span>
        </div>
        <div class="mt-5 flex-grow flex flex-col">
          <nav class="flex-1 px-2 pb-4 space-y-1">
            <router-link
              v-for="link in links"
              :key="link.route"
              :to="{ name: link.route }"
              class="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              :class="{ 'bg-gray-200 dark:bg-gray-600': $route.name === link.route }"
            >
              <span class="mr-2 flex-shrink-0 h-6 w-6 text-gray-600 dark:text-gray-200">
                <component :is="link.icon || span" />
              </span>
              <span>{{ link.title }}</span>
            </router-link>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlitzLogo from './BlitzLogo.vue'
import { useBlitzStore } from '../stores/BlitzStore'
import {
  CalendarIcon,
  UserIcon,
  IdentificationIcon,
  BriefcaseIcon,
  AtSymbolIcon,
  DocumentCheckIcon,
  ShoppingCartIcon,
  ArchiveBoxIcon,
  UserPlusIcon,
  CubeIcon,
  ChatBubbleBottomCenterTextIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'

export default {
  components: {
    BlitzLogo,
    UserIcon,
    CalendarIcon,
    IdentificationIcon,
    BriefcaseIcon,
    AtSymbolIcon,
    DocumentCheckIcon,
    ShoppingCartIcon,
    ArchiveBoxIcon,
    UserPlusIcon,
    CubeIcon,
    ChatBubbleBottomCenterTextIcon,
    ChartBarIcon,
    CurrencyDollarIcon,
    TruckIcon,
    XMarkIcon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['hide'],
  computed: {
    links () {
      const store = useBlitzStore()

      return [
        { title: 'Agenda', icon: 'CalendarIcon', route: 'blitz.home' },
        { title: 'Alunos', icon: 'UserIcon', route: 'blitz.customers.index', hide: ['basic'].includes(store.user?.type) },
        { title: 'Aulas de Direção', icon: 'IdentificationIcon', route: 'blitz.lessons.index' },
        // { title: 'Campanhas', icon: 'MegaphoneIcon', route: 'blitz.campaigns.index' },
        { title: 'Contas a Pagar', icon: 'BriefcaseIcon', route: 'blitz.bills-to-pay.index', hide: ['assistant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Equipe', icon: 'AtSymbolIcon', route: 'blitz.users.index', hide: ['accountant', 'assistant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Exames', icon: 'DocumentCheckIcon', route: 'blitz.exams.index' },
        { title: 'Fornecedores', icon: 'ShoppingCartIcon', route: 'blitz.suppliers.index', hide: ['assistant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Histórico', icon: 'ArchiveBoxIcon', route: 'blitz.activities.index', hide: ['accountant', 'assistant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Indicações', icon: 'UserPlusIcon', route: 'blitz.indications.index', hide: ['basic'].includes(store.user?.type) },
        { title: 'Itens', icon: 'CubeIcon', route: 'blitz.items.index' },
        { title: 'Leads', icon: 'ChatBubbleBottomCenterTextIcon', route: 'blitz.leads.index', hide: ['accountant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Métricas', icon: 'ChartBarIcon', route: 'blitz.metrics', hide: ['assistant', 'basic', 'instructor'].includes(store.user?.type) },
        { title: 'Pedidos', icon: 'CurrencyDollarIcon', route: 'blitz.orders.index', hide: ['basic', 'instructor'].includes(store.user?.type) },
        { title: 'Veículos', icon: 'TruckIcon', route: 'blitz.vehicles.index' }
      ].filter(link => !link.hide)
    }
  }
}
</script>
